import React from 'react';
import { Helmet } from 'react-helmet-async';

function DisclaimerPage() {
  return (
    <div className="min-h-screen bg-slate-900 text-white py-10 px-4">
      <Helmet>
        <title>Data Disclaimer — CommunityGuardAPI</title>
        <link rel="canonical" href="https://communityguardapi.com/disclaimer" />
        <meta name="description" content="Data accuracy notice and disclaimer for CommunityGuardAPI sex offender registry data." />
        <meta property="og:title" content="Data Disclaimer — CommunityGuardAPI" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://communityguardapi.com/disclaimer" />
      </Helmet>
      <div className="max-w-4xl mx-auto">

        {/* Page Header */}
        <h1 className="text-4xl md:text-5xl font-extrabold text-center mb-2">
          Data Disclaimer & Accuracy Notice
        </h1>
        <p className="text-center text-slate-400 mb-10 text-sm">
          Last updated: March 2026
        </p>

        {/* ============================================================ */}
        {/* CRITICAL WARNING BOX */}
        {/* ============================================================ */}
        <div className="bg-red-950/60 border-2 border-red-500 rounded-xl p-6 md:p-8 mb-10 shadow-lg shadow-red-900/20">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 text-red-400 text-4xl font-bold leading-none mt-1" aria-hidden="true">
              !!
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-red-400 mb-3 uppercase tracking-wide">
                Critical Notice
              </h2>
              <p className="text-lg md:text-xl text-red-100 font-semibold leading-relaxed">
                The data on this website is provided for <span className="underline decoration-red-400 decoration-2">INFORMATIONAL PURPOSES ONLY</span>.
                It is <span className="underline decoration-red-400 decoration-2">NOT</span> a substitute for official sex offender registries
                maintained by federal, state, or local government agencies.
              </p>
              <p className="text-red-200 mt-3 text-base">
                You must always verify information through official sources before making any decisions
                based on the data presented here.
              </p>
            </div>
          </div>
        </div>

        {/* ============================================================ */}
        {/* DATA SOURCES */}
        {/* ============================================================ */}
        <section className="bg-slate-800 border border-slate-700 rounded-xl p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold mb-4 text-white">Data Sources</h2>
          <p className="text-slate-300 mb-4">
            CommunityGuardAPI aggregates publicly available sex offender registry data from the following sources:
          </p>
          <ul className="space-y-3 text-slate-300">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-2 h-2 rounded-full bg-emerald-500 mt-2"></span>
              <span>
                <strong className="text-white">National Sex Offender Public Website (NSOPW)</strong> &mdash;
                the U.S. Department of Justice&apos;s centralized registry at{' '}
                <a
                  href="https://www.nsopw.gov"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-500 hover:text-amber-400 underline transition-colors"
                >
                  nsopw.gov
                </a>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-2 h-2 rounded-full bg-emerald-500 mt-2"></span>
              <span>
                <strong className="text-white">Individual state sex offender registries</strong> &mdash;
                direct data from state-level agencies across the United States
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-2 h-2 rounded-full bg-emerald-500 mt-2"></span>
              <span>
                <strong className="text-white">Data refresh schedule:</strong> Registry data is refreshed
                approximately <strong className="text-white">once per month</strong>. Records may not reflect
                real-time changes to official registries.
              </span>
            </li>
          </ul>
        </section>

        {/* ============================================================ */}
        {/* KNOWN LIMITATIONS */}
        {/* ============================================================ */}
        <section className="bg-slate-800 border border-slate-700 rounded-xl p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold mb-4 text-white">Known Limitations</h2>
          <p className="text-amber-400 font-semibold mb-4 text-sm uppercase tracking-wide">
            Please read carefully before using this data
          </p>
          <ul className="space-y-3 text-slate-300">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-5 h-5 rounded bg-amber-600/30 text-amber-400 text-xs flex items-center justify-center mt-0.5 font-bold">1</span>
              <span>
                <strong className="text-white">Records may be outdated.</strong> Offenders may have moved to a new address,
                been reclassified to a different risk level, been removed from a registry, or become deceased since the data was last updated.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-5 h-5 rounded bg-amber-600/30 text-amber-400 text-xs flex items-center justify-center mt-0.5 font-bold">2</span>
              <span>
                <strong className="text-white">State data varies significantly.</strong> Not all states provide the same
                level of detail. Some states may not include risk levels, photographs, complete addresses, or offense descriptions.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-5 h-5 rounded bg-amber-600/30 text-amber-400 text-xs flex items-center justify-center mt-0.5 font-bold">3</span>
              <span>
                <strong className="text-white">Risk level classifications differ by state.</strong> Some states use tiered
                systems (Tier 1, 2, 3), others use labels (Low, Moderate, High), and some states do not classify offenders
                by risk level at all.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-5 h-5 rounded bg-amber-600/30 text-amber-400 text-xs flex items-center justify-center mt-0.5 font-bold">4</span>
              <span>
                <strong className="text-white">Address geocoding is approximate.</strong> Map markers may be placed at
                approximate locations based on geocoded addresses. A marker&apos;s position should not be interpreted as
                the exact residence of an individual.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-5 h-5 rounded bg-amber-600/30 text-amber-400 text-xs flex items-center justify-center mt-0.5 font-bold">5</span>
              <span>
                <strong className="text-white">Incarcerated offenders may appear at prison addresses.</strong> Some records
                reflect the location of a correctional facility rather than a community address.
              </span>
            </li>
          </ul>
        </section>

        {/* ============================================================ */}
        {/* PROHIBITED USES  —  Red-tinted */}
        {/* ============================================================ */}
        <section className="bg-red-950/40 border border-red-800/60 rounded-xl p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold mb-4 text-red-400">
            What This Data Should NOT Be Used For
          </h2>
          <p className="text-red-200/80 mb-4">
            Using the information on this website for any of the following purposes is strictly prohibited
            and may violate federal and/or state law:
          </p>
          <ul className="space-y-3 text-red-100">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 text-red-500 font-bold mt-0.5">X</span>
              <span>
                <strong>Making safety decisions</strong> without independently verifying the information through
                official government registries
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 text-red-500 font-bold mt-0.5">X</span>
              <span>
                <strong>Harassing, threatening, intimidating, or committing any act of violence</strong> against
                any person listed on this website
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 text-red-500 font-bold mt-0.5">X</span>
              <span>
                <strong>Employment, housing, or insurance decisions</strong> &mdash; this data is not suitable for
                background checks or screening purposes
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 text-red-500 font-bold mt-0.5">X</span>
              <span>
                <strong>Any illegal purpose</strong> whatsoever, including but not limited to stalking, vandalism,
                or discrimination
              </span>
            </li>
          </ul>
        </section>

        {/* ============================================================ */}
        {/* RECOMMENDED ACTIONS  —  Teal/green-tinted */}
        {/* ============================================================ */}
        <section className="bg-emerald-950/30 border border-emerald-800/40 rounded-xl p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold mb-4 text-emerald-400">
            What You Should Do
          </h2>
          <ul className="space-y-3 text-slate-300">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 text-emerald-400 font-bold mt-0.5 text-lg leading-none">&#10003;</span>
              <span>
                <strong className="text-white">Always verify information</strong> at{' '}
                <a
                  href="https://www.nsopw.gov"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-500 hover:text-amber-400 underline transition-colors"
                >
                  nsopw.gov
                </a>{' '}
                or your state&apos;s official sex offender registry before relying on any data.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 text-emerald-400 font-bold mt-0.5 text-lg leading-none">&#10003;</span>
              <span>
                <strong className="text-white">Report any inaccuracies</strong> you find to us at{' '}
                <a
                  href="mailto:info@asan.digital"
                  className="text-emerald-500 hover:text-amber-400 underline transition-colors"
                >
                  info@asan.digital
                </a>{' '}
                so we can investigate and correct the data.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 text-emerald-400 font-bold mt-0.5 text-lg leading-none">&#10003;</span>
              <span>
                <strong className="text-white">Contact local law enforcement</strong> if you have safety concerns
                about a registered sex offender in your area.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 text-emerald-400 font-bold mt-0.5 text-lg leading-none">&#10003;</span>
              <span>
                <strong className="text-white">Use this as a starting point for awareness</strong>, not as a
                definitive or comprehensive source of information.
              </span>
            </li>
          </ul>
        </section>

        {/* ============================================================ */}
        {/* LIMITATION OF LIABILITY */}
        {/* ============================================================ */}
        <section className="bg-slate-800 border border-slate-700 rounded-xl p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold mb-4 text-white">Limitation of Liability</h2>
          <p className="text-slate-300 leading-relaxed">
            Asan Digital LLC provides the data and services on this website <strong className="text-white">&quot;AS IS&quot;</strong> and{' '}
            <strong className="text-white">&quot;AS AVAILABLE&quot;</strong> without warranty of any kind, whether express or implied,
            including but not limited to the implied warranties of merchantability, fitness for a particular purpose, accuracy,
            completeness, or non-infringement.
          </p>
          <p className="text-slate-300 leading-relaxed mt-4">
            Asan Digital LLC, its officers, directors, employees, and agents shall not be held liable for any damages,
            losses, claims, or expenses arising from or related to your use of, or reliance upon, the data presented
            on this website. This includes, without limitation, any direct, indirect, incidental, consequential, or
            punitive damages.
          </p>
          <p className="text-slate-300 leading-relaxed mt-4">
            You acknowledge and agree that your use of this website and the data contained herein is at your sole risk.
          </p>
        </section>

        {/* ============================================================ */}
        {/* LEGAL COMPLIANCE  —  Amber warning */}
        {/* ============================================================ */}
        <section className="bg-amber-950/30 border border-amber-700/50 rounded-xl p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold mb-4 text-amber-400">Legal Compliance</h2>
          <p className="text-amber-100 leading-relaxed font-medium">
            Under federal law and the laws of most states, it is illegal to use sex offender registry information
            to commit crimes against registered sex offenders, including but not limited to harassment, threats,
            intimidation, assault, or vandalism.
          </p>
          <p className="text-amber-200/80 leading-relaxed mt-4">
            Violations may be prosecuted under federal and/or state law. By using this website, you acknowledge
            that you understand these restrictions and agree to comply with all applicable laws.
          </p>
        </section>

        {/* ============================================================ */}
        {/* REPORT INACCURACIES */}
        {/* ============================================================ */}
        <section className="bg-slate-800 border border-slate-700 rounded-xl p-6 md:p-8 mb-10 text-center">
          <h2 className="text-2xl font-bold mb-3 text-white">Report Inaccuracies</h2>
          <p className="text-slate-300 mb-5 max-w-2xl mx-auto">
            If you believe any information on this website is inaccurate, outdated, or incorrect,
            please contact us so we can review and correct the record.
          </p>
          <a
            href="mailto:info@asan.digital"
            className="inline-block px-8 py-3 bg-emerald-500 hover:bg-amber-400 text-white font-semibold rounded-lg transition-colors duration-200 shadow-lg"
          >
            Contact Us: info@asan.digital
          </a>
        </section>

        {/* Footer note */}
        <p className="text-center text-xs text-slate-500 pb-6">
          &copy; {new Date().getFullYear()} Asan Digital LLC. All rights reserved. &mdash; CommunityGuardAPI
        </p>

      </div>
    </div>
  );
}

export default DisclaimerPage;
